import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { ListItem } from "./types";

dayjs.extend(advancedFormat);

export const dateHelpers = {
    dateMonthsAgo,
    toIsoString,
    toDate,
    toDisplayString,
    toShortDateString,
    toDatetTimeString,
    toDateString,
    toIsoString2,
    formatDate,
    toTime,
    minutesToTimestamp,
    getFirstDayOfWeek,
    isSameDay,
    getWeekId,
    getDateFromWeekId,
    getDateDifferenceInDays,
};

export const currencyHelpers = {
    toCurrency,
};

export const objHelpers = {
    deepClone,
};

export const passwordHelpers = {
    generateRandomString,
};

export const enumHelpers = {
    createListItemsFromEnum,
};

function dateMonthsAgo(months: number) {
    var d = new Date();
    d.setMonth(d.getMonth() - months);
    return d;
}

function toIsoString(value?: Date, includeTime?: boolean) {
    return value ? dayjs(value).format(includeTime ? "YYYY-MM-DDTHH:mm:ss" : "YYYY-MM-DD") : "";
}
function formatDate(date: string | Date, format: string) {
    return dayjs(date).format(format);
}

function toDate(valueStr: string) {
    return new Date(valueStr);
}

function toDisplayString(date: string) {
    return dayjs(date).format("DD/MM/YY h:mm A");
}

function toDatetTimeString(date: string, isoTime: boolean = false) {
    return isoTime ? dayjs(date).format("DD/MM/YYYY HH:mm") : dayjs(date).format("DD/MM/YYYY h:mm A");
}

function toTime(date: string, ios: boolean = false) {
    return (date && dayjs(date).format(ios ? "HH:mm" : "h:mm A")) || "";
}

function toDateString(date: string | Date) {
    return dayjs(date).format("DD/MM/YYYY");
}

function toShortDateString(date: string) {
    return dayjs(date).format("DD/MM/YY");
}

function toIsoString2(date: string) {
    return dayjs(date).format("YYYY-MM-DD");
}

function getDateDifferenceInDays(a: Date, b: Date) {
    const diffTime = Math.abs(a.getTime() - b.getTime());
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

function minutesToTimestamp(minutes: number, hideSuffix: boolean = false): string {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    const formattedHours = hours.toString().padStart(2, "0");
    const formattedMinutes = remainingMinutes.toString().padStart(2, "0");
    return `${formattedHours}:${formattedMinutes}${hideSuffix ? "" : " Hr"}`;
}

function getFirstDayOfWeek(date: Date): Date {
    const firstDayOfWeek = new Date(date);
    const dayOfWeek = firstDayOfWeek.getDay();
    const diff = firstDayOfWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    firstDayOfWeek.setDate(diff);
    return firstDayOfWeek;
}

function isSameDay(a: Date, b: Date) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// Baseline date for week calculation - January 4, 1970 (first Sunday of Unix epoch week)
const WEEK_BASELINE_DATE = new Date(1970, 0, 4); // January 4, 1970 (Sunday)

/**
 * Gets the first day (Sunday) of the week for a given date
 * @param date - The date to get the first day of week for
 * @returns Date object representing the Sunday of that week
 */
function getFirstDayOfWeekSunday(date: Date): Date {
    const firstDayOfWeek = new Date(date);
    const dayOfWeek = firstDayOfWeek.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const diff = firstDayOfWeek.getDate() - dayOfWeek;
    firstDayOfWeek.setDate(diff);
    return firstDayOfWeek;
}

/**
 * Generates a unique week ID based on the number of weeks from baseline date (January 4, 1970)
 * Similar to epoch time but for weeks. Week starts on Sunday and ends on Saturday.
 * @param date - The date to get the week ID for
 * @returns Week ID as number (0 for baseline week, positive for future weeks, negative for past weeks)
 */
function getWeekId(date: Date | string): number {
    const targetDate = typeof date === "string" ? new Date(date) : new Date(date);
    const firstDayOfWeek = getFirstDayOfWeekSunday(targetDate);

    // Calculate the difference in milliseconds
    const diffMs = firstDayOfWeek.getTime() - WEEK_BASELINE_DATE.getTime();

    // Convert to weeks (7 days * 24 hours * 60 minutes * 60 seconds * 1000 milliseconds)
    const diffWeeks = Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000));

    return diffWeeks;
}

/**
 * Converts a week ID back to the Sunday date of that week
 * @param weekId - The week ID to convert
 * @returns Date object representing the Sunday of that week
 */
function getDateFromWeekId(weekId: number): Date {
    const result = new Date(WEEK_BASELINE_DATE);
    result.setDate(result.getDate() + weekId * 7);
    return result;
}

function toCurrency(amount: number, hideDecimals: boolean = false) {
    if (hideDecimals) amount = Math.round(amount);
    return amount.toFixed(hideDecimals ? 0 : 2).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
}

function deepClone<T>(obj: T) {
    return JSON.parse(JSON.stringify(obj)) as T;
}

function generateRandomString(length: number): string {
    const uppercase = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const lowercase = "abcdefghjkmnpqrstuvwxyz";
    const numbers = "23456789";
    const allCharacters = uppercase + lowercase + numbers;

    if (length < 3) {
        throw new Error("Length should be at least 3 to include one character from each group.");
    }

    let result = "";
    result += uppercase.charAt(Math.floor(Math.random() * uppercase.length));
    result += lowercase.charAt(Math.floor(Math.random() * lowercase.length));
    result += numbers.charAt(Math.floor(Math.random() * numbers.length));

    for (let i = 3; i < length; i++) {
        result += allCharacters.charAt(Math.floor(Math.random() * allCharacters.length));
    }

    return shuffleString(result);
}

function shuffleString(str: string): string {
    const array = str.split("");
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
    return array.join("");
}

export function createListItemsFromEnum(enumObj: any): ListItem[] {
    return Object.keys(enumObj)
        .filter((key) => isNaN(Number(key)))
        .map((key) => ({
            key: enumObj[key],
            value: key.replaceAll("_", " "),
        }));
}

// Combines a primary list (e.g. active entities) with a secondary list (e.g. entities referenced
// by a search result) so items only present in the secondary list - such as inactive projects or
// employees that still have historical records - aren't dropped from the consolidated list.
export function mergeListItems(primary: ListItem[], secondary: ListItem[]): ListItem[] {
    const primaryKeys = new Set(primary.map((item) => item.key));
    const additionalItems = secondary.filter((item) => !primaryKeys.has(item.key));
    return [...primary, ...additionalItems];
}
